import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "@/components/ui/sheet";
import { Phone, MapPin, Storefront, CalendarBlank, Paperclip, User } from "@phosphor-icons/react";
import { toast } from "sonner";
import PhotoLightbox from "@/components/PhotoLightbox";
import { RichNotes, extractImagesFromNotes } from "./RichNotes";
import { PRIORITIES } from "./constants";

export function CardDetailDialog({ card, onClose, onSave, canManageOwner, sellers, onReassign, readOnly = false }) {
  const initialTitle = card?.title || card?.client?.display_name || card?.client?.name || "";
  const [title, setTitle] = useState(initialTitle);
  const [notes, setNotes] = useState(card?.notes || "");
  const [priority, setPriority] = useState(card?.priority || "");
  const [value, setValue] = useState(card?.transaction_value || 0);
  const [currency, setCurrency] = useState(card?.value_currency || "MXN");
  const [scheduledAt, setScheduledAt] = useState(card?.scheduled_at || "");
  const [ownerUserId, setOwnerUserId] = useState(card?.owner_user_id || "");
  const [saving, setSaving] = useState(false);
  const [lb, setLb] = useState({ open: false, index: 0 });

  if (!card) return null;
  const isClientCard = !!card.client;

  const openLightboxAt = (src) => {
    const all = extractImagesFromNotes(notes);
    const idx = all.indexOf(src);
    setLb({ open: true, index: idx >= 0 ? idx : 0 });
  };

  const submit = async () => {
    setSaving(true);
    const fields = {
      notes,
      priority,
      transaction_value: Number(value) || 0,
      value_currency: currency,
      scheduled_at: scheduledAt || "",
    };
    if (!isClientCard) fields.title = (title || "").trim() || "Sin nombre";
    try {
      await onSave(fields);
      if (canManageOwner && ownerUserId !== (card?.owner_user_id || "")) {
        await onReassign(ownerUserId);
      }
      toast.success("Tarjeta actualizada");
      onClose();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={!!card} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0" data-testid="card-detail-dialog">
        <div className="p-6 space-y-4">
          <SheetHeader>
            <SheetTitle className="font-heading text-2xl text-left flex items-center gap-2">
              Detalle de tarjeta
              {readOnly && <span className="text-[10px] font-mono-data font-semibold px-2 py-0.5 rounded-sm bg-zinc-100 text-zinc-600">📁 SOLO LECTURA</span>}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Nombre / Título</label>
              {isClientCard ? (
                <div className="mt-1 text-sm font-semibold">{title} <span className="text-[10px] text-zinc-400 ml-2">(cliente registrado)</span></div>
              ) : (
                <Input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="mt-1"
                  disabled={readOnly}
                  readOnly={readOnly}
                  data-testid="detail-title"
                />
              )}
            </div>
            {card.client && (
              <div className="text-[11px] text-zinc-500 space-y-0.5">
                {card.client.business_name && <div><Storefront size={11} className="inline mr-1" />{card.client.business_name}</div>}
                {card.client.city && <div><MapPin size={11} className="inline mr-1" />{card.client.city}</div>}
                {card.client.phone && <div><Phone size={11} className="inline mr-1" />{card.client.phone}</div>}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Prioridad</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  disabled={readOnly}
                  className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-sm bg-white disabled:opacity-60 disabled:bg-zinc-50"
                  data-testid="detail-priority"
                >
                  {PRIORITIES.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Valor de transacción</label>
                <div className="mt-1 flex gap-1">
                  <select
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    disabled={readOnly}
                    className="border border-zinc-300 rounded px-1 py-1.5 text-sm bg-white disabled:opacity-60 disabled:bg-zinc-50"
                    data-testid="detail-currency"
                  >
                    <option value="MXN">MX$</option>
                    <option value="USD">US$</option>
                  </select>
                  <Input
                    type="number"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    disabled={readOnly}
                    readOnly={readOnly}
                    className="flex-1"
                    data-testid="detail-value"
                  />
                </div>
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <label className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                  <CalendarBlank size={11} weight="bold" /> Programar acción / recordatorio
                </label>
                {scheduledAt && !readOnly && (
                  <button type="button" onClick={() => setScheduledAt("")} className="text-[10px] text-[#E0000F] hover:underline" data-testid="detail-clear-schedule">
                    Quitar
                  </button>
                )}
              </div>
              <input
                type="datetime-local"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                disabled={readOnly}
                className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-sm bg-white disabled:opacity-60 disabled:bg-zinc-50"
                data-testid="detail-scheduled-at"
              />
              <p className="text-[10px] text-zinc-400 mt-1">Esta tarjeta aparecerá en la vista de Calendario el día seleccionado.</p>
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Notas</label>
                <span className="text-[10px] text-zinc-400 inline-flex items-center gap-1"><Paperclip size={10} /> Pega screenshots con <span className="font-mono-data px-1 bg-zinc-100 rounded">Ctrl+V</span></span>
              </div>
              <RichNotes
                initial={card?.notes || ""}
                onChange={setNotes}
                onImageClick={openLightboxAt}
                placeholder="Escribe detalles, próximos pasos, cotizaciones… y pega capturas (Ctrl+V) donde quieras."
                testId="detail-notes"
                readOnly={readOnly}
              />
            </div>

            {canManageOwner && (
              <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50">
                <label className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 flex items-center gap-1.5">
                  <User size={11} weight="bold" /> Vendedor asignado
                </label>
                <select
                  value={ownerUserId}
                  onChange={(e) => setOwnerUserId(e.target.value)}
                  className="mt-1 w-full border border-zinc-300 rounded px-2 py-1.5 text-sm bg-white"
                  data-testid="detail-owner-select"
                >
                  <option value="">— Sin asignar —</option>
                  {(sellers || []).map((s) => (
                    <option key={s.id} value={s.id}>{s.name} · {s.email}</option>
                  ))}
                </select>
                <p className="text-[10px] text-zinc-500 mt-1">
                  {ownerUserId && ownerUserId !== (card?.owner_user_id || "")
                    ? "Al guardar, la tarjeta se moverá al CRM del vendedor seleccionado."
                    : "Solo un supervisor o super admin puede reasignar."}
                </p>
              </div>
            )}

            <div className="text-[10px] font-mono-data text-zinc-400">
              Creada: {card.created_at ? new Date(card.created_at).toLocaleString("es-MX") : "—"}
              {card.owner_name && <span className="ml-2">· Vendedor: <b>{card.owner_name}</b></span>}
            </div>
          </div>
        </div>
        <SheetFooter className="px-6 pb-6 pt-2 border-t border-zinc-100 sticky bottom-0 bg-white">
          <Button variant="outline" onClick={onClose} data-testid="detail-cancel">{readOnly ? "Cerrar" : "Cancelar"}</Button>
          {!readOnly && (
            <Button onClick={submit} disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="detail-save">
              {saving ? "Guardando…" : "Guardar"}
            </Button>
          )}
        </SheetFooter>
        <PhotoLightbox
          open={lb.open}
          onClose={() => setLb((s) => ({ ...s, open: false }))}
          photos={extractImagesFromNotes(notes)}
          initialIndex={lb.index}
          title={`Adjunto · ${initialTitle}`}
          filenameBase={`adjunto_${initialTitle || "tarjeta"}`}
        />
      </SheetContent>
    </Sheet>
  );
}
